import React, { useEffect, useState } from "react";
import { Plus, Save, Tags } from "lucide-react";
import { api, formatZAR } from "../api";
import { toast } from "sonner";

const REPEATS = [
  { key: "monthly", label: "Monthly" },
  { key: "annual", label: "Annual" },
  { key: "once_off", label: "Once-off" },
];

export default function Pricing() {
  const [cats, setCats] = useState([]);
  const [tab, setTab] = useState("monthly");
  const [editing, setEditing] = useState({});
  const [newCat, setNewCat] = useState({ name: "", default_price: 0, default_repeat: "monthly" });

  const load = async () => {
    const r = await api.get("/task-categories");
    setCats(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (cat) => {
    const e = editing[cat.id] || {};
    try {
      await api.patch(`/task-categories/${cat.id}`, {
        name: e.name ?? cat.name,
        default_price: Number(e.default_price ?? cat.default_price),
        default_repeat: e.default_repeat ?? cat.default_repeat,
      });
      toast.success("Saved");
      setEditing(s => { const cp = { ...s }; delete cp[cat.id]; return cp; });
      load();
    } catch { toast.error("Failed to save"); }
  };

  const addNew = async () => {
    if (!newCat.name.trim()) return;
    try {
      await api.post("/task-categories", {
        name: newCat.name.trim(),
        default_price: Number(newCat.default_price) || 0,
        default_repeat: newCat.default_repeat,
      });
      setNewCat({ name: "", default_price: 0, default_repeat: tab });
      toast.success("Category added");
      load();
    } catch { toast.error("Failed"); }
  };

  const filtered = cats.filter(c => c.default_repeat === tab);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="mb-6">
        <div className="np-label mb-2">Pricing index</div>
        <h1 className="text-3xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
          Standard prices per task
        </h1>
        <p className="text-[#595959] mt-2">These are the default suggestions. Per-client overrides are remembered automatically when you enter work.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-[#CCCCCC]">
        {REPEATS.map(r => (
          <button
            key={r.key}
            data-testid={`pricing-tab-${r.key}`}
            onClick={() => setTab(r.key)}
            className={`px-4 py-2.5 -mb-px border-b-2 font-semibold text-sm ${
              tab === r.key ? "border-[#00ACB3] text-[#0F4F51]" : "border-transparent text-[#595959] hover:text-[#011617]"
            }`}
          >{r.label}</button>
        ))}
      </div>

      <div className="np-card mb-6">
        <table className="w-full text-sm">
          <thead className="np-table-header">
            <tr>
              <th className="text-left px-4 py-3">Task category</th>
              <th className="text-right px-4 py-3">Standard price</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={3} className="text-center text-[#595959] py-8">No categories yet.</td></tr>
            )}
            {filtered.map(c => {
              const e = editing[c.id] || {};
              const isEditing = !!editing[c.id];
              return (
                <tr key={c.id} className="border-b border-[#CCCCCC] hover:bg-[#E5FFFF]/40">
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <input
                        data-testid={`edit-name-${c.id}`}
                        value={e.name ?? c.name}
                        onChange={ev => setEditing(s => ({ ...s, [c.id]: { ...s[c.id], name: ev.target.value } }))}
                        className="w-full px-2 py-1 rounded border border-[#CCCCCC] outline-none focus:border-[#00ACB3]"
                      />
                    ) : (
                      <span className="font-semibold text-[#011617] inline-flex items-center gap-2">
                        <Tags className="w-4 h-4 text-[#0F4F51]" /> {c.name}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <input
                        data-testid={`edit-price-${c.id}`}
                        type="number"
                        step="0.01"
                        value={e.default_price ?? c.default_price}
                        onChange={ev => setEditing(s => ({ ...s, [c.id]: { ...s[c.id], default_price: ev.target.value } }))}
                        className="w-32 px-2 py-1 rounded border border-[#CCCCCC] text-right outline-none focus:border-[#00ACB3]"
                      />
                    ) : (
                      <span className="font-bold text-[#011617]">{formatZAR(c.default_price)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <button
                        data-testid={`save-cat-${c.id}`}
                        onClick={() => save(c)}
                        className="inline-flex items-center gap-1 bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-3 py-1.5 text-xs"
                      ><Save className="w-3.5 h-3.5" /> Save</button>
                    ) : (
                      <button
                        data-testid={`edit-cat-${c.id}`}
                        onClick={() => setEditing(s => ({ ...s, [c.id]: { name: c.name, default_price: c.default_price, default_repeat: c.default_repeat } }))}
                        className="text-xs font-semibold text-[#00ACB3] hover:text-[#0F4F51]"
                      >Edit</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add new */}
      <div className="np-card">
        <h2 className="text-lg font-bold text-[#011617] mb-3" style={{ fontFamily: "Manrope" }}>Add a new task category</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            data-testid="new-cat-name"
            value={newCat.name}
            onChange={e => setNewCat({ ...newCat, name: e.target.value })}
            placeholder="Task name"
            className="md:col-span-2 px-3 py-2.5 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] bg-white"
          />
          <input
            data-testid="new-cat-price"
            type="number"
            step="0.01"
            value={newCat.default_price}
            onChange={e => setNewCat({ ...newCat, default_price: e.target.value })}
            placeholder="Standard price"
            className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] bg-white"
          />
          <select
            data-testid="new-cat-repeat"
            value={newCat.default_repeat}
            onChange={e => setNewCat({ ...newCat, default_repeat: e.target.value })}
            className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] bg-white"
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
            <option value="once_off">Once-off</option>
          </select>
        </div>
        <button
          data-testid="new-cat-add-btn"
          onClick={addNew}
          className="mt-3 bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-5 py-2.5 inline-flex items-center gap-2"
        ><Plus className="w-4 h-4" /> Add category</button>
      </div>
    </div>
  );
}
